import { toNestErrors } from "@hookform/resolvers";
import { deepSanitizeWithExclusions } from "@congrs/utils";
import { appendErrors } from "react-hook-form";
import ajv from "./ajvInstance.js";

// English texts for the validation keys. An app that passes its i18next `t`
// to the resolver gets these keys in the user's language instead; without one
// (apps/web) the English is used as is.
const DEFAULT_MESSAGES = {
  "validation.required": "This field is required",
  "validation.min_length": "Must be at least {{count}} characters",
  "validation.max_length": "Must be no more than {{count}} characters",
  "validation.minimum": "Must be at least {{count}}",
  "validation.maximum": "Must be no more than {{count}}",
  "validation.invalid_email": "Invalid email address",
  "validation.invalid_date": "Invalid date",
  "validation.invalid_date_time": "Invalid date/time",
  "validation.invalid_format": "Invalid format",
  "validation.invalid_value": "Invalid value",
  "validation.not_allowed": "Must be one of the allowed values",
  "validation.value_mismatch": "Value does not match",
};

const englishT = (key, values = {}) =>
  DEFAULT_MESSAGES[key].replace(/\{\{(\w+)\}\}/g, (_, name) =>
    String(values[name] ?? "")
  );

const formatMessage = (format, t) => {
  switch (format) {
    case "email":
      return t("validation.invalid_email");
    case "date":
      return t("validation.invalid_date");
    case "iso-date-time":
      return t("validation.invalid_date_time");
    default:
      return t("validation.invalid_format");
  }
};

const formatErrorMessage = (error, t) => {
  const { keyword, params, data } = error;

  switch (keyword) {
    case "errorMessage":
      return error.message;
    case "required":
    case "i18nRequired":
      return t("validation.required");
    case "minLength":
      return params.limit === 1
        ? t("validation.required")
        : t("validation.min_length", { count: params.limit });
    case "maxLength":
      return t("validation.max_length", { count: params.limit });
    case "minimum":
      return t("validation.minimum", { count: params.limit });
    case "maximum":
      return t("validation.maximum", { count: params.limit });
    case "format":
      return formatMessage(params.format, t);
    case "pattern":
      return t("validation.invalid_format");
    case "type":
      return data === null || data === undefined || data === ""
        ? t("validation.required")
        : t("validation.invalid_value");
    case "enum":
      return t("validation.not_allowed");
    case "const":
      return t("validation.value_mismatch");
    default:
      return error.message;
  }
};

const parseErrorSchema = (ajvErrors, validateAllFieldCriteria, data, t) => {
  ajvErrors.forEach((error) => {
    if (error.keyword === "required") {
      error.instancePath += "/" + error.params.missingProperty;
    }
    // For i18nRequired errors, set the path to the first language code field
    if (error.keyword === "i18nRequired") {
      // Get the i18n object from the data using the error path
      const pathParts = error.instancePath.substring(1).split("/");
      let i18nData = data;
      for (const part of pathParts) {
        i18nData = i18nData?.[part];
      }
      // Use the first language key if available, otherwise default to 'en'
      const firstLangKey =
        i18nData && typeof i18nData === "object"
          ? Object.keys(i18nData)[0] || "en"
          : "en";
      error.instancePath += "/" + firstLangKey;
    }
  });

  return ajvErrors.reduce((previous, error) => {
    const path = error.instancePath.substring(1).replace(/\//g, ".");

    if (!previous[path]) {
      previous[path] = {
        message: formatErrorMessage(error, t),
        type: error.keyword,
      };
    }

    if (validateAllFieldCriteria) {
      const types = previous[path].types;
      const messages = types && types[error.keyword];

      previous[path] = appendErrors(
        path,
        validateAllFieldCriteria,
        previous,
        error.keyword,
        messages
          ? [].concat(messages, formatErrorMessage(error, t) || "")
          : formatErrorMessage(error, t)
      );
    }

    return previous;
  }, {});
};

/**
 * @param schema AJV schema for the form values
 * @param {{ t?: (key: string, values?: any) => string }} [options]
 *   `t` translates the `validation.*` messages; English when left out.
 */
const resolver = (schema, { t = englishT } = {}) => {
  const validate = ajv.compile(schema);

  return (data, _, options) => {
    const cleaned = deepSanitizeWithExclusions(data);
    const valid = validate(cleaned);

    const result = valid
      ? { values: cleaned, errors: {} }
      : {
          values: {},
          errors: toNestErrors(
            parseErrorSchema(
              validate.errors,
              !options.shouldUseNativeValidation &&
                options.criteriaMode === "all",
              cleaned,
              t
            ),
            options
          ),
        };

    return result;
  };
};

export default resolver;
